import { ReviewableDocumentCategoryKey } from '@bordrio/shared';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router';
import { ErrorableLoader } from '@src/components';
import {
  useGetNifOrderRowQuery,
  useGetNifOrderWithReviewableDocumentsQuery,
} from '@src/generated/graphql';
import { getCurrentLocale } from '@src/i18n';
import NotFoundPage from '@src/pages/NotFound.page';
import { useReuploadDocumentsFlow } from '../../documents/hooks/useReuploadDocumentsFlow';
import NifOrderLayout from '../components/NifOrderLayout';
import { NifOrderStepPageWithReuploadIntakeDocumentProps } from '../types/stepProps';
import { NifOrderReuploadIntakePage } from './reupload-intake.page';

const DOCUMENT_CATEGORY_ORDER: ReviewableDocumentCategoryKey[] = [
  'nif_proof_of_identity',
  'nif_portuguese_birth_certificate',
  'nif_proof_of_address',
  'nif_minor_birth_certificate',
  'nif_parent_proof_of_identity',
  'nif_additional_document',
];

function ReuploadIntakePage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { i18n } = useTranslation();

  // The reupload flow is entered from the dashboard when an admin has rejected
  // intake documents and the order has moved to AWAITING_INTAKE. The heavy
  // query spreads private fields gated to the draft/intake window, so we wait
  // for status confirmation before firing it.
  const { data: rowData, error: rowError } = useGetNifOrderRowQuery({
    variables: { id },
  });
  const isAwaitingIntake = rowData?.nifOrder.status === 'AWAITING_INTAKE';

  const { data, error } = useGetNifOrderWithReviewableDocumentsQuery({
    variables: {
      id,
      locale: getCurrentLocale(i18n),
    },
    skip: !isAwaitingIntake,
  });

  const documents = data?.nifOrder.intakeDocuments;

  const flowResult = useReuploadDocumentsFlow({
    documents,
    extractReviewableDocument: (document) => document.reviewableDocument,
    extractTitle: (document) => document.titleTranslated,
    pathPrefix: `/orders/nif/reupload-intake/${id}`,
    reviewerType: 'ADMIN',
    documentCategoryOrder: DOCUMENT_CATEGORY_ORDER,
  });
  const navigate = useNavigate();

  if (rowData && !isAwaitingIntake) {
    return <Navigate replace to="/" />;
  }

  if (flowResult.type === 'pending' || !data) {
    return <ErrorableLoader error={error ?? rowError} />;
  }

  if (flowResult.type === 'redirect') {
    return <Navigate replace to={flowResult.redirectTo} />;
  }

  const stepProps: NifOrderStepPageWithReuploadIntakeDocumentProps = {
    nifOrder: data.nifOrder,
    intakeDocument: flowResult.document,
    previousUrl: flowResult.previousUrl,
    getNextUrl: flowResult.getNextUrl,
    navigateToNextStep() {
      void navigate(flowResult.getNextUrl());
    },
    isReupload: true,
  };

  return (
    <Routes>
      <Route
        element={
          <NifOrderLayout
            onExitClick={() => navigate('/')}
            steps={flowResult.sidebarSteps}
          />
        }
      >
        <Route
          path=":documentId"
          element={
            <NifOrderReuploadIntakePage
              {...stepProps}
              key={flowResult.document.id}
            />
          }
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default ReuploadIntakePage;
